import { useState, useRef, useEffect, KeyboardEvent, ClipboardEvent } from 'react';

export interface CodeInputProps {
  length?: number;
  onChange: (code: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: boolean;
}

/**
 * CodeInput - Reusable 6-digit code input component
 * 
 * Features:
 * - Auto-advance to next input when digit entered
 * - Paste support: paste 6-digit code → fills all boxes
 * - Backspace handling: clear current box, move to previous
 * - Keyboard navigation: arrow keys move between inputs
 * - WCAG 2.1 AA compliant: aria-labels, keyboard accessible, screen reader support
 */
export const CodeInput = ({
  length = 6,
  onChange,
  autoFocus = false,
  disabled = false,
  error = false,
}: CodeInputProps) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Notify parent when code changes
  useEffect(() => {
    const code = values.join('');
    onChange(code);
  }, [values, onChange]);

  // Auto-focus first input on mount if autoFocus is true
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleChange = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) {
      return;
    }

    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    // Auto-advance to next input if digit entered
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (values[index]) {
        // Clear current input
        const newValues = [...values];
        newValues[index] = '';
        setValues(newValues);
      } else if (index > 0) {
        // Move to previous input and clear it
        const newValues = [...values];
        newValues[index - 1] = '';
        setValues(newValues);
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      return;
    }

    // Handle delete key
    if (e.key === 'Delete') {
      const newValues = [...values];
      newValues[index] = '';
      setValues(newValues);
      return;
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only process if pasted data is all digits and matches length
    if (/^\d+$/.test(pastedData) && pastedData.length === length) {
      const newValues = pastedData.split('').slice(0, length);
      setValues(newValues);

      // Focus last input after paste
      const lastInput = inputRefs.current[length - 1];
      if (lastInput) {
        lastInput.focus();
      }
    }
  };

  const handleFocus = (index: number) => {
    // Select all text when input is focused
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex gap-2 justify-center" role="group" aria-label="Verification code input">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={values[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${length}`}
          aria-invalid={error}
          className={`
            w-12 h-12 text-center text-h2 font-semibold
            border-2 rounded-md
            focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
            transition-colors
            ${error
              ? 'border-error bg-red-50 text-error'
              : 'border-border text-text-primary'
            }
            ${disabled
              ? 'bg-background-secondary cursor-not-allowed opacity-50'
              : 'bg-background hover:border-primary'
            }
          `}
        />
      ))}
    </div>
  );
};

